import { CUSTOM_SECTION, LIST_SECTIONS, newId } from './sectionDefs.js'
import { FONTS, FONT_SIZES, GAPS } from './resumeLayout.js'

// Makes whatever JSON a user typed into a resume document the preview can draw: every section
// present, every field the right type, defaults for what is missing. It does NOT cap lengths or
// counts: the server's ResumeContent is the authority on those and trims on save.
//
// `previous` is the content being replaced. An entry typed without an id reuses the id of the
// entry at the same place, so the form's cards do not all reset on every keystroke.

const BASICS = ['full_name', 'headline', 'email', 'phone', 'location', 'website', 'linkedin', 'github']
// The order a new resume shows them in (ResumeContent::SECTION_KEYS): projects right after experience, then education, skills and the rest.
const SECTION_KEYS = ['summary', 'experience', 'projects', 'education', 'skills', 'certifications', 'languages']
const PICTURE_SHAPES = ['circle', 'rounded', 'square']
const DEFAULT_ACCENT = '#2563eb'

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
const text = (value) => (typeof value === 'string' ? value : '')
const textList = (value) => (Array.isArray(value) ? value.filter((item) => typeof item === 'string') : [])
const list = (value) => (Array.isArray(value) ? value : [])
const idOf = (value, fallback) => (typeof value === 'string' && value ? value : fallback || newId())
const choice = (value, allowed, fallback) => (allowed.includes(value) ? value : fallback)

function entryOf(raw, def, previous) {
  const source = isObject(raw) ? raw : {}
  const entry = { id: idOf(source.id, previous?.id) }
  def.fields.forEach((field) => {
    if (field.type === 'dates') {
      entry.start_date = text(source.start_date)
      entry.end_date = text(source.end_date)
      if (field.current) entry.current = source.current === true
    } else if (field.type === 'bullets' || field.type === 'tags') {
      entry[field.key] = textList(source[field.key])
    } else {
      entry[field.key] = text(source[field.key])
    }
  })
  return entry
}

const entriesOf = (raw, def, previous) => list(raw).map((entry, index) => entryOf(entry, def, previous?.[index]))

export function completeContent(raw, previous = null) {
  const source = isObject(raw) ? raw : {}
  const basics = isObject(source.basics) ? source.basics : {}

  const content = {
    basics: {
      ...Object.fromEntries(BASICS.map((key) => [key, text(basics[key])])),
      links: list(basics.links).map((link) => ({ label: text(link?.label), url: text(link?.url) })),
    },
    summary: text(source.summary),
  }
  Object.keys(LIST_SECTIONS).forEach((key) => { content[key] = entriesOf(source[key], LIST_SECTIONS[key], previous?.[key]) })

  content.custom = list(source.custom).map((section, index) => {
    const own = isObject(section) ? section : {}
    const before = previous?.custom?.[index]
    return { id: idOf(own.id, before?.id || `custom-${Math.random().toString(36).slice(2, 10)}`), title: text(own.title), entries: entriesOf(own.entries, CUSTOM_SECTION, before?.entries) }
  })

  const settings = isObject(source.settings) ? source.settings : {}
  const known = [...SECTION_KEYS, ...content.custom.map((section) => section.id)]
  const order = textList(settings.section_order).filter((key) => known.includes(key))
  content.settings = {
    accent: /^#[0-9a-f]{6}$/i.test(settings.accent) ? settings.accent.toLowerCase() : DEFAULT_ACCENT,
    font: choice(settings.font, Object.keys(FONTS), 'sans'),
    font_size: choice(settings.font_size, Object.keys(FONT_SIZES), 'medium'),
    spacing: choice(settings.spacing, Object.keys(GAPS), 'normal'),
    picture_shape: choice(settings.picture_shape, PICTURE_SHAPES, 'circle'),
    show_picture: settings.show_picture !== false,
    section_order: [...new Set([...order, ...known])],
    hidden_sections: [...new Set(textList(settings.hidden_sections).filter((key) => known.includes(key)))],
  }
  return content
}
